import { ExpandMore } from '@mui/icons-material';
import { Paper, Table, TableCell, TableContainer, TableRow, TableHead, TableBody, Tab, Typography, Box, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import * as React from 'react';
import { QueryResult, StoreInfo } from 'src/model';

interface OverviewPanelProps {
    show: boolean;
    ocdbDatasets?: QueryResult|null;
    fidraddbDatasets?: string[]|null;
    loading?: boolean;
    error?: string|null;
    loadOverview?: () => void;
    serverInfo: StoreInfo;
}

class OverviewPanel extends React.PureComponent<OverviewPanelProps> {
    constructor(props: OverviewPanelProps) {
        super(props);
    }

    private getOcdbPaths(): string[] {
        const ds = this.props.ocdbDatasets?.datasets ?? [];
        return ds.map((d: any) => d?.path).filter((p: any): p is string => Boolean(p));
    }

    private getFidraddbFiles(): string[] {
        return this.props.fidraddbDatasets ?? [];
    }

    private getOcdbCounts(idx: number): { ref: string; count: number }[] {
        const counts: Record<string, number> = {};
        for (const p of this.getOcdbPaths()) {
            const s = (p || "").split("/")[idx] || "";
            counts[s] = (counts[s] ?? 0) + 1;
        }
        return Object.entries(counts)
            .map(([ref, count]) => ({ ref, count }))
            .sort((a, b) => b.count - a.count);
    }


    private parseFidrad(file: string) {
        const base = (file || "").replace(/\.[^.]+$/i, "");
        const parts = base.split("_");
        if (parts.length < 2) return null;
      
        const source = parts[0];
        const radiometer = parts[1].replace(/\d+$/,"");

        const upper = base.toUpperCase();
        const m = upper.match(/(?:^|_)(E_CLASS|L_CLASS)(?:_|$)/);
        const className = m ? m[1] : "-";
      
        const KEYWORDS = ["STRAY","THERMAL","RADCAL","POLAR","ANGULAR"];
        const joined = parts.join("_").toUpperCase();
        const fileType = KEYWORDS.find(k => joined.includes(k)) ?? "-";
      
        return { source, radiometer, className, fileType };
      }

      

    private getFidradCounts(): { source:string; radiometer:string; className:string; fileType:string; count:number }[] {
        const acc = new Map<string, { source:string; radiometer:string; className:string; fileType:string; count:number }>();
        for (const f of this.getFidraddbFiles()) {
          const p = this.parseFidrad(f);
          if (!p) continue;
          const key = `${p.source}|${p.radiometer}|${p.className}|${p.fileType}`;
          const cur = acc.get(key) ?? { ...p, count: 0 };
          cur.count += 1;
          acc.set(key, cur);
        }
        return Array.from(acc.values()).sort((a,b) =>
          a.source.localeCompare(b.source) ||
          a.radiometer.localeCompare(b.radiometer) ||
          a.className.localeCompare(b.className) ||
          a.fileType.localeCompare(b.fileType)
        );
      }

    render() {
        if(!this.props.show) return null;
        
        const rowsAffiliation = this.getOcdbCounts(0);
        const rowsExperiment = this.getOcdbCounts(1);
        const rowsCruise = this.getOcdbCounts(2);
        const rowsFidrad = this.getFidradCounts();
        const totalAffilitations = rowsAffiliation.reduce((s,r)=>s+r.count,0);
        const totalExperiments = rowsExperiment.reduce((s,r)=>s+r.count,0);
        const totalCruises = rowsCruise.reduce((s,r)=>s+r.count,0);
        const totalFidrad = rowsFidrad.reduce((s,r)=>s+r.count,0);
        const { productGroups, productNameDatasetCounts } = this.props.serverInfo;

        return (
            <Box>
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                        <Typography variant="h6" sx={{ mb: 1 }}>OCDB – Number of in situ datasets per affiliation</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer component={Paper}>
                            <Table size="small">
                            <TableHead>
                                <TableRow>
                                <TableCell>Affiliation</TableCell>
                                <TableCell align="right">Count</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rowsAffiliation.map((r, i) => (
                                <TableRow key={i}>
                                    <TableCell>{r.ref}</TableCell>
                                    <TableCell align="right">{r.count}</TableCell>
                                </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Sum</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{totalAffilitations}</TableCell>
                                </TableRow>
                            </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                        <Typography variant="h6" sx={{ mb: 1 }}>OCDB – Number of in situ datasets per experiment</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer component={Paper}>
                            <Table size="small">
                            <TableHead>
                                <TableRow>
                                <TableCell>Experiment</TableCell>
                                <TableCell align="right">Count</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rowsExperiment.map((r, i) => (
                                <TableRow key={i}>
                                    <TableCell>{r.ref}</TableCell>
                                    <TableCell align="right">{r.count}</TableCell>
                                </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Sum</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{totalExperiments}</TableCell>
                                </TableRow>
                            </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
                
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                    <Typography variant="h6" sx={{ mb: 1 }}>OCDB – Number of in situ datasets per cruise</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer component={Paper}>
                            <Table size="small">
                            <TableHead>
                                <TableRow>
                                <TableCell>Cruise</TableCell>
                                <TableCell align="right">Count</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rowsCruise.map((r, i) => (
                                <TableRow key={i}>
                                    <TableCell>{r.ref}</TableCell>
                                    <TableCell align="right">{r.count}</TableCell>
                                </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Sum</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{totalCruises}</TableCell>
                                </TableRow>
                            </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
                
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                        <Typography variant="h6" sx={{ mb: 1 }}>OCDB – Number of in situ datasets per product group</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                <TableRow>
                                    <TableCell>Group</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell align="right">Count</TableCell>
                                </TableRow>
                                </TableHead>

                                <TableBody>
                                {productGroups.flatMap((g) =>
                                    g.products.map((p, idx) => (
                                    <TableRow key={`${g.name}-${p}`}>
                                        {idx === 0 && (
                                        <TableCell rowSpan={g.products.length} sx={{ fontWeight: 700, verticalAlign: "top" }}>
                                            {g.name}
                                        </TableCell>
                                        )}
                                        <TableCell sx={{ pl: 3 }}>{p}</TableCell>
                                        <TableCell align="right">{productNameDatasetCounts?.[p] ?? 0}</TableCell>
                                    </TableRow>
                                    ))
                                )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
                
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore/>}>
                        <Typography variant="h6" sx={{ mb: 1 }}>FidRadDB – Number of calibration and characterisation files</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer component={Paper}>
                            <Table size="small">
                            <TableHead>
                                <TableRow>
                                <TableCell>Source</TableCell>
                                <TableCell>Radiometer</TableCell>
                                <TableCell>Class</TableCell>
                                <TableCell>File type</TableCell>
                                <TableCell align="right">Count</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rowsFidrad.map((r,i)=>(
                                <TableRow key={i}>
                                    <TableCell>{r.source}</TableCell>
                                    <TableCell>{r.radiometer}</TableCell>
                                    <TableCell>{r.className}</TableCell>
                                    <TableCell>{r.fileType}</TableCell>
                                    <TableCell align="right">{r.count}</TableCell>
                                </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Sum</TableCell>
                                    <TableCell />
                                    <TableCell />
                                    <TableCell />
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{totalFidrad}</TableCell>
                                </TableRow>
                            </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            </Box>
        )
    }
}

export default OverviewPanel;